import React, { useCallback, useContext, useMemo } from 'react';
import { Flex, List, useScreenSize } from '@banhala/ui-kit';
import { ScrollContext } from '../../../../../context/ScrollContext';
import { useGoodsDetailAtomics } from '../queries/useGoodsDetailAtomics';
import { isNil } from 'shared/src/utils';
import { useGoodsSno } from '../../../../../hooks/useGoodsSno';
import { useIsSectionVisible } from '../../../../../store/sectionVisibility';
import { AtomicComponent } from 'shared/src/component/AtomicRenderer/utils/createAtomicComponent';
import { useLikeMap, useOutfitBookmarkMap } from 'shared/src/hooks';
import { AtomicScreenProvider } from 'shared/src/component/AtomicRenderer/context/AtomicScreenContext';
import { useGoodsSnoKey } from '../../../../../hooks/useGoodsSnoKey';

const ProxySkeleton = () => {
  const { width: screenWidth } = useScreenSize();

  const cardWidth = useMemo(() => {
    return (screenWidth - 2) / 3;
  }, [screenWidth]);

  return (
    <Flex>
      <Flex.Row
        padding={{ horizontal: 16, vertical: 16 }}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        <Flex
          backgroundColor={'bg_tertiary'}
          width={98}
          height={24}
          borderRadius={4}
        ></Flex>
        <Flex
          backgroundColor={'bg_tertiary'}
          width={64}
          height={18}
          borderRadius={4}
        ></Flex>
      </Flex.Row>
      <Flex>
        <List<number>
          data={Array.from({ length: 9 }, (_, index) => index)}
          numColumns={3}
          columnWrapperStyle={{ gap: 2 }}
          renderItem={({ item }) => {
            return (
              <Flex flex={1} position={'relative'} key={item}>
                <Flex
                  width={cardWidth}
                  height={cardWidth * 1.2}
                  backgroundColor={'bg_tertiary'}
                />
                <Flex margin={{ top: 4, bottom: 20 }} gap={2}>
                  <Flex
                    width={cardWidth * 0.8}
                    height={20}
                    backgroundColor={'bg_tertiary'}
                  />
                  <Flex
                    width={cardWidth * 0.9}
                    height={16}
                    backgroundColor={'bg_tertiary'}
                  />
                  <Flex
                    width={cardWidth * 0.4}
                    height={14}
                    margin={{ top: 4 }}
                    backgroundColor={'bg_tertiary'}
                  />
                </Flex>
              </Flex>
            );
          }}
        />
      </Flex>
    </Flex>
  );
};

export const GoodsAtomicRenderer = React.memo<{
  focused: boolean;
  screen: string;
  onChangeHeight: (height: number) => void;
}>(props => {
  const { onChangeHeight } = props;
  const key = useGoodsSnoKey('atomic');
  const sno = useGoodsSno();

  const isVisible = useIsSectionVisible(key);
  const { isScrolling } = useContext(ScrollContext);
  const { likeMap, setLikeMap } = useLikeMap({});
  const { bookmarkMap, setBookmarkMap } = useOutfitBookmarkMap({});
  const extraParams = useMemo(() => ({ goods_sno: String(sno) }), [sno]);

  const { data: atomic } = useGoodsDetailAtomics(isVisible);

  // const ref = useRef<ListRef<IScreensComponent>>(null);

  const handleLayout = useCallback(
    ({ nativeEvent }: { nativeEvent: { layout: { height: number } } }) => {
      onChangeHeight(nativeEvent.layout.height);
    },
    [onChangeHeight],
  );

  if (isNil(atomic)) {
    if (!props.focused) return null;

    return <ProxySkeleton />;
  }

  return (
    <Flex onLayout={handleLayout}>
      <AtomicScreenProvider value={'GOODS_DETAIL'}>
        {atomic.components.map((component, index) => (
          <AtomicComponent
            key={`Goods-Atomic-${index}`}
            component={component}
            screen={'GOODS_DETAIL'}
            index={index}
            likeMap={likeMap}
            setLikeMap={setLikeMap}
            bookmarkMap={bookmarkMap}
            setBookmarkMap={setBookmarkMap}
            extraParams={extraParams}
            parentIsScrolling={isScrolling}
          />
        ))}
      </AtomicScreenProvider>
    </Flex>
  );
});
